import { useQuery } from '@apollo/client'
import { useCallback, useMemo } from 'react'
import { ISSUE_FIELD_CONFIGS_QUERY } from './queries'
import { ALWAYS_ON_ISSUE_FIELDS } from './types'
import type {
  IssueFieldConfig,
  IssueFieldConfigsQuery,
  IssueFormFieldValue,
  IssueFormMode,
} from './types'

export interface UseIssueFieldConfig {
  configs: IssueFieldConfig[]
  loading: boolean
  /**
   * Changes whenever the resolved configuration changes. Views re-run
   * `validateForm()` on it so requirements that arrive after first paint are
   * reflected in `formValid`.
   */
  version: string
  isVisible: (field: IssueFormFieldValue, mode: IssueFormMode) => boolean
  isRequired: (field: IssueFormFieldValue, mode: IssueFormMode) => boolean
  labelFor: (field: IssueFormFieldValue, fallback: string) => string
}

const alwaysOn = new Set<IssueFormFieldValue>(ALWAYS_ON_ISSUE_FIELDS)

/**
 * The issue form configuration for a category, falling back to the company
 * standard when `categoryId` is empty — which is what the create form shows
 * until a category has been picked.
 */
export function useIssueFieldConfig(
  categoryId?: string | null
): UseIssueFieldConfig {
  const { data, loading } = useQuery<IssueFieldConfigsQuery>(
    ISSUE_FIELD_CONFIGS_QUERY,
    {
      variables: { category: categoryId || null },
      fetchPolicy: 'cache-first',
    }
  )

  const configs = useMemo(() => data?.issueFieldConfigs ?? [], [data])

  const byField = useMemo(() => {
    const map = new Map<IssueFormFieldValue, IssueFieldConfig>()
    for (const config of configs) map.set(config.field, config)
    return map
  }, [configs])

  const isVisible = useCallback(
    (field: IssueFormFieldValue, mode: IssueFormMode) => {
      if (alwaysOn.has(field)) return true
      const config = byField.get(field)
      // Unknown field (config not loaded yet, or one the backend does not
      // consider configurable): show it. Hiding on a miss would make the form
      // flicker empty on every cold load.
      if (!config) return true
      return mode === 'create' ? config.visibleOnCreate : config.visibleOnEdit
    },
    [byField]
  )

  const isRequired = useCallback(
    (field: IssueFormFieldValue, mode: IssueFormMode) => {
      if (alwaysOn.has(field)) return true
      const config = byField.get(field)
      if (!config) return false
      return mode === 'create' ? config.requiredOnCreate : config.requiredOnEdit
    },
    [byField]
  )

  const labelFor = useCallback(
    (field: IssueFormFieldValue, fallback: string) =>
      byField.get(field)?.labelOverride || fallback,
    [byField]
  )

  const version = useMemo(
    () =>
      configs
        .map(
          config =>
            `${
              config.field
            }:${+config.visibleOnCreate}${+config.requiredOnCreate}` +
            `${+config.visibleOnEdit}${+config.requiredOnEdit}`
        )
        .join('|'),
    [configs]
  )

  return { configs, loading, version, isVisible, isRequired, labelFor }
}
